/**
 * TopUpModalContext
 *
 * App-wide access to the in-place wallet top-up modal. Any component beneath
 * the provider can call `useTopUp().open(reason?)` to pop the modal WITHOUT
 * navigating away from the current page.
 *
 * Mounting: place the provider high in the tree — inside WalletSummaryProvider
 * and OLVRateProvider so the modal has wallet + rate context (see App.tsx). The
 * provider renders the modal itself inside a Stripe <Elements> boundary, and
 * only while it is open, so:
 *   - stripe.js is fetched lazily the first time a user opens the modal, and
 *   - the whole app tree does NOT need to be wrapped in <Elements>.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { getSharedStripePromise } from '../components/wallet/sharedStripe';
import { TopUpModal } from '../components/wallet/TopUpModal';

interface TopUpContextValue {
  /** Whether the modal is currently open. */
  isOpen: boolean;
  /** The reason passed to the most recent open() call, if any. */
  reason: string | null;
  /** Open the modal. `reason` is free-text for UX/telemetry. */
  open: (reason?: string) => void;
  /** Close the modal. */
  close: () => void;
}

const TopUpModalContext = createContext<TopUpContextValue | null>(null);

// Stable options reference — a changing object would re-init <Elements>.
const ELEMENTS_OPTIONS = { appearance: { variables: { fontFamily: 'inherit' } } } as const;

export function TopUpModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState<string | null>(null);

  const open = useCallback((r?: string) => {
    setReason(r ?? null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo<TopUpContextValue>(
    () => ({ isOpen, reason, open, close }),
    [isOpen, reason, open, close],
  );

  return (
    <TopUpModalContext.Provider value={value}>
      {children}
      {isOpen && (
        <Elements stripe={getSharedStripePromise()} options={ELEMENTS_OPTIONS}>
          <TopUpModal reason={reason} onClose={close} />
        </Elements>
      )}
    </TopUpModalContext.Provider>
  );
}

/** Access the top-up modal controls. Throws if used outside the provider. */
export function useTopUp(): TopUpContextValue {
  const ctx = useContext(TopUpModalContext);
  if (!ctx) {
    throw new Error('useTopUp must be used within a TopUpModalProvider');
  }
  return ctx;
}

/** Non-throwing variant for components that may render outside the provider (e.g. tests). */
export function useMaybeTopUp(): TopUpContextValue | null {
  return useContext(TopUpModalContext);
}

export default TopUpModalContext;
